import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CitySelect } from "@/components/Selects/CitySelect";
import type { CityOption } from "@/components/Selects/CitySelect";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import { HealthcareProfessionalTypeSelect } from "@/components/Selects/HealthcareProfessionalTypeSelect";
import { USStateSelect } from "@/components/Selects/USStateSelect";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc } from "@/trpc";
import { EmploymentTypeSelect } from "@/components/Selects/EmploymentTypeSelect";
import { EmploymentType, Permission } from "@api/shared-runtime/enums";
import { useOrganization } from "@/hooks/use-organization";
import { useRolePermissions } from "@/hooks/use-role-permissions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChatThreadBody } from "@/features/chat/chat-thread-body";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OrganizationProviderChatButton } from "@/features/chat/organization-provider-chat-button";

const searchFormSchema = z.object({
  specialties: z
    .array(z.string())
    .min(1, "Select at least one specialty")
    .default([]),
  professionalTypes: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    )
    .default([]),
  city: z
    .array(
      z.object({
        name: z.string(),
        lat: z.number().optional(),
        lng: z.number().optional(),
      }),
    )
    .optional(),
  distance: z.string().optional(),
  employmentTypeIds: z.array(z.nativeEnum(EmploymentType)).optional(),
  stateLicenses: z.array(z.string()).optional(),
});

type SearchFormData = z.infer<typeof searchFormSchema>;
type ParsedProfessionalType = { id: string; name: string };
const isParsedProfessionalType = (
  value: unknown,
): value is ParsedProfessionalType =>
  typeof value === "object" &&
  value !== null &&
  "id" in value &&
  "name" in value &&
  typeof value.id === "string" &&
  typeof value.name === "string";

function ProviderSearch() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { selectedOrgBusinessUnit } = useOrganization();
  const queryClient = useQueryClient();
  const { data: user } = useQuery(trpc.user.getUser.queryOptions());
  const { hasPermission } = useRolePermissions(user);
  const canManageChat = hasPermission(Permission.ManageChat);
  const parsedSpecialties =
    search.specialties?.split(",").filter(Boolean) ??
    (search.specialty ? [search.specialty] : []);

  let parsedProfessionalTypes: { id: string; name: string }[] = [];
  if (search.professionalTypes) {
    try {
      const values = JSON.parse(search.professionalTypes);
      if (Array.isArray(values)) {
        parsedProfessionalTypes = values.filter(isParsedProfessionalType);
      }
    } catch {
      parsedProfessionalTypes = [];
    }
  } else if (search.professionalTypeId) {
    parsedProfessionalTypes = [
      {
        id: search.professionalTypeId,
        name: search.professionalTypeName || "",
      },
    ];
  }

  const parsedStateLicenses =
    search.stateLicenses?.split(",").filter(Boolean) ?? [];

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<SearchFormData>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      specialties: parsedSpecialties.length > 0 ? parsedSpecialties : [],
      professionalTypes: parsedProfessionalTypes,
      city: search.cityName
        ? [
            {
              name: search.cityName || "",
              lat: Number(search.lat) || undefined,
              lng: Number(search.lng) || undefined,
            },
          ]
        : [],
      distance: search.distance || "unlimited",
      employmentTypeIds: search.employmentTypeIds
        ? (search.employmentTypeIds.split(",") as EmploymentType[])
        : undefined,
      stateLicenses:
        parsedStateLicenses.length > 0 ? parsedStateLicenses : undefined,
    },
  });

  const [searchParams, setSearchParams] = useState<{
    lat?: number;
    lng?: number;
    specialtyCodes: string[];
    distanceMiles?: number;
    employmentTypeIds?: EmploymentType[];
    healthcareProfessionalTypeIds?: string[];
    stateLicenses?: string[];
  } | null>(() => {
    if (parsedSpecialties.length > 0) {
      return {
        lat: search.lat ? Number(search.lat) : undefined,
        lng: search.lng ? Number(search.lng) : undefined,
        specialtyCodes: parsedSpecialties,
        distanceMiles: search.distance ? Number(search.distance) : undefined,
        employmentTypeIds: search.employmentTypeIds
          ? (search.employmentTypeIds.split(",") as EmploymentType[])
          : undefined,
        healthcareProfessionalTypeIds:
          parsedProfessionalTypes.length > 0
            ? parsedProfessionalTypes.map((type) => type.id)
            : undefined,
        stateLicenses:
          parsedStateLicenses.length > 0 ? parsedStateLicenses : undefined,
      };
    }
    return null;
  });
  const [chatDialogUser, setChatDialogUser] = useState<{
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null>(null);
  const [chatDialogThreadId, setChatDialogThreadId] = useState<string | null>(
    null,
  );
  const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
  const [chatLoadingUserId, setChatLoadingUserId] = useState<string | null>(
    null,
  );

  const ensureChatMutation = useMutation(
    trpc.organization.ensureProviderSearchChat.mutationOptions({
      onError: () => {
        toast.error("Unable to open chat. Please try again.");
      },
    }),
  );

  const handleOpenChat = async (provider: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  }) => {
    setChatLoadingUserId(provider.id);
    try {
      const result = await ensureChatMutation.mutateAsync({
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
        providerUserId: provider.id,
      });
      setChatDialogUser(provider);
      setChatDialogThreadId(result.threadId);
      setIsChatDialogOpen(true);
      await queryClient.invalidateQueries(
        trpc.chat.listChatThreadMessages.queryOptions({
          threadId: result.threadId,
        }),
      );
    } catch {
      toast.error("Unable to open chat. Please try again.");
    } finally {
      setChatLoadingUserId(null);
    }
  };

  const handleChatDialogChange = (open: boolean) => {
    if (!open) {
      setIsChatDialogOpen(false);
      setChatDialogThreadId(null);
      setChatDialogUser(null);
    } else {
      setIsChatDialogOpen(true);
    }
  };

  const { data: providers = [], isLoading } = useQuery({
    ...trpc.organization.searchProviders.queryOptions({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      lat: searchParams?.lat,
      lng: searchParams?.lng,
      specialtyCodes: searchParams?.specialtyCodes ?? [],
      distanceMiles: searchParams?.distanceMiles,
      healthcareProfessionalTypeIds:
        searchParams?.healthcareProfessionalTypeIds,
      employmentTypeIds: searchParams?.employmentTypeIds,
      stateLicenses: searchParams?.stateLicenses,
    }),
    enabled: !!searchParams?.specialtyCodes?.length,
  });

  const distanceOptions = [
    { value: "50", label: "50 miles" },
    { value: "100", label: "100 miles" },
    { value: "300", label: "300 miles" },
  ];

  const onSubmit = (data: SearchFormData) => {
    const city = data.city?.[0];
    const specialtyCodes = data.specialties;
    const professionalTypeSelections = data.professionalTypes ?? [];
    const professionalTypeIds = professionalTypeSelections.map(
      (type) => type.id,
    );

    const params = {
      lat: city?.lat,
      lng: city?.lng,
      specialtyCodes,
      distanceMiles:
        data.distance && data.distance !== "unlimited"
          ? parseInt(data.distance)
          : undefined,
      employmentTypeIds: data.employmentTypeIds,
      healthcareProfessionalTypeIds:
        professionalTypeIds.length > 0 ? professionalTypeIds : undefined,
      stateLicenses:
        data.stateLicenses && data.stateLicenses.length > 0
          ? data.stateLicenses
          : undefined,
    };

    setSearchParams(params);

    navigate({
      search: (prev) => ({
        ...prev,
        specialties: specialtyCodes.join(","),
        lat: city?.lat ? String(city.lat) : prev.lat,
        lng: city?.lng ? String(city.lng) : prev.lng,
        cityName: city?.name ?? prev.cityName,
        distance:
          data.distance && data.distance !== "unlimited"
            ? data.distance
            : prev.distance,
        professionalTypes:
          professionalTypeSelections.length > 0
            ? JSON.stringify(professionalTypeSelections)
            : prev.professionalTypes,
        employmentTypeIds:
          data.employmentTypeIds && data.employmentTypeIds.length > 0
            ? data.employmentTypeIds.join(",")
            : prev.employmentTypeIds,
        stateLicenses:
          data.stateLicenses && data.stateLicenses.length > 0
            ? data.stateLicenses.join(",")
            : undefined,
      }),
    });
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <Card className="p-6">
        <h1 className="text-2xl font-bold mb-3">Find Healthcare Providers</h1>
        <p className="mb-6 max-w-3xl text-sm text-gray-600">
          Search for providers who match your needs, review their preferences,
          and start a chat from the current business unit.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <label className="block text-sm font-medium mb-2">
                Professional Type (Optional)
              </label>
              <HealthcareProfessionalTypeSelect
                isMulti
                value={watch("professionalTypes")}
                onChange={(value) =>
                  setValue(
                    "professionalTypes",
                    (value as { id: string; name: string }[] | undefined) ?? [],
                    {
                      shouldValidate: true,
                    },
                  )
                }
                placeholder="Select professional type"
              />
            </div>

            <div className="md:col-span-4">
              <label className="block text-sm font-medium mb-2">
                Specialties
              </label>
              <SpecialtiesSelect
                value={watch("specialties")}
                isMulti
                onChange={(value) =>
                  setValue("specialties", (value as string[] | null) ?? [], {
                    shouldValidate: true,
                  })
                }
              />
              {errors.specialties && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.specialties.message}
                </p>
              )}
            </div>

            <div className="md:col-span-4">
              <label className="block text-sm font-medium mb-2">
                City (Optional)
              </label>
              <CitySelect
                value={watch("city") || []}
                onChange={(value: CityOption[]) =>
                  setValue("city", value, { shouldValidate: true })
                }
              />
              {errors.city && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.city.message}
                </p>
              )}
            </div>
            <div className="md:col-span-4">
              <label className="block text-sm font-medium mb-2">
                State License (Optional)
              </label>
              <USStateSelect
                multiple
                value={watch("stateLicenses") ?? []}
                onChange={(value) =>
                  setValue("stateLicenses", value as string[], {
                    shouldValidate: true,
                  })
                }
                placeholder="Any state"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Employment Type
              </label>
              <EmploymentTypeSelect
                value={watch("employmentTypeIds")}
                onChange={(value) => setValue("employmentTypeIds", value)}
                placeholder="Any type"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Distance (Optional)
              </label>
              <Select
                value={watch("distance")}
                onValueChange={(value) =>
                  setValue("distance", value, { shouldValidate: true })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select distance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unlimited">No limit</SelectItem>
                  {distanceOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.distance && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.distance.message}
                </p>
              )}
            </div>
          </div>

          <Button type="submit" className="w-full py-8">
            Search Providers
          </Button>
        </form>

        <div className="mt-8">
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : !searchParams ? (
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Enter Search Parameters
              </h3>
              <p className="text-gray-500">
                Select a specialty above to find providers. City and distance
                are optional.
              </p>
            </div>
          ) : providers.length > 0 ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Found {providers.length} provider
                  {providers.length !== 1 ? "s" : ""}
                </h2>
              </div>
              {providers.map((provider) => {
                const displayName =
                  [provider.firstName, provider.lastName]
                    .filter((part) => part && part.length > 0)
                    .join(" ")
                    .trim() || "Provider";

                return (
                  <Card key={provider.id} className="p-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div>
                        <h3 className="text-lg font-semibold">{displayName}</h3>
                        <p>
                          {provider.specialties.map((s) => s.name).join(", ")}
                        </p>
                      </div>
                      {canManageChat && (
                        <OrganizationProviderChatButton
                          canChat={provider.canChat}
                          onClick={() => handleOpenChat(provider)}
                          isLoading={chatLoadingUserId === provider.id}
                          className="shrink-0"
                        />
                      )}
                    </div>
                    <div className="mt-3">
                      {provider.previousOrganizations?.length > 0 && (
                        <p className="text-sm">
                          <span className="text-gray-500">Orgs:</span>{" "}
                          {provider.previousOrganizations?.map(
                            (org, i: number) => (
                              <span key={i}>
                                {String(org.value || "")}
                                {i <
                                  provider.previousOrganizations.length - 1 &&
                                  " • "}
                              </span>
                            ),
                          )}
                        </p>
                      )}
                      <p className="text-sm">
                        <span className="text-gray-500">
                          Preferred locations:
                        </span>{" "}
                        {provider.locations
                          ?.filter((l) => l.name !== "ANY_CITY")
                          ?.map((l, i: number) => (
                            <span key={l.name}>
                              {l.name}
                              {i <
                                provider.locations.filter(
                                  (l) => l.name !== "ANY_CITY",
                                ).length -
                                  1 && " • "}
                            </span>
                          ))}
                        {provider.locations?.some(
                          (l) => l.name === "ANY_CITY",
                        ) && (
                          <span className="text-gray-500">
                            {" "}
                            (open to other cities)
                          </span>
                        )}
                      </p>

                      <div className="flex flex-col gap-1">
                        <p className="text-sm">
                          <span className="text-gray-500">
                            Employment types:
                          </span>{" "}
                          <Popover>
                            <PopoverTrigger>
                              <span className="font-bold underline decoration-dotted cursor-help">
                                {provider.employmentTypes.length === 0
                                  ? "Any Type"
                                  : provider.employmentTypes
                                      .map((type) => type.displayText)
                                      .join(" • ")}
                              </span>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 rounded-md bg-white p-4 shadow-md border border-gray-200">
                              <div className="flex gap-2 items-center">
                                <p className="text-sm text-gray-600">
                                  This provider's availability preference may
                                  change. Please confirm during outreach.
                                </p>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-500">
                            Practice settings:
                          </span>{" "}
                          {provider.practiceSettings.length === 0
                            ? "Any Setting"
                            : provider.practiceSettings
                                .map((setting) => setting.displayText)
                                .join(" • ")}
                        </p>
                        {provider.availabilityDescription && (
                          <p className="text-sm">
                            <span className="text-gray-500">Availability:</span>{" "}
                            {provider.availabilityDescription}
                          </p>
                        )}
                        <p className="text-sm">
                          <span className="text-gray-500">IMLC License:</span>{" "}
                          <span className="font-semibold">
                            {provider.hasImlcLicense ? "Yes" : "No"}
                          </span>
                        </p>
                        <div className="text-sm">
                          <span className="text-gray-500">State licenses:</span>{" "}
                          {provider.stateLicenses.length > 0 ? (
                            <span className="inline-flex flex-wrap gap-1 ml-1">
                              {provider.stateLicenses.map((state) => (
                                <span
                                  key={state}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                >
                                  {state}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <span className="text-gray-400">
                              No state licenses
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No providers found
              </h3>
              <p className="text-gray-500 mb-4">
                Try adjusting your search criteria:
              </p>
              <ul className="text-gray-600 space-y-2">
                <li>• Expand your search radius</li>
                <li>• Try different specialties</li>
                <li>• Search in nearby cities</li>
              </ul>
            </div>
          )}
        </div>
      </Card>
      {chatDialogUser && (
        <ProviderChatDialog
          open={isChatDialogOpen}
          onOpenChange={handleChatDialogChange}
          user={chatDialogUser}
          threadId={chatDialogThreadId}
        />
      )}
    </div>
  );
}

const ProviderChatDialog = ({
  open,
  onOpenChange,
  user,
  threadId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: { id: string; firstName: string | null; lastName: string | null };
  threadId: string | null;
}) => {
  const queryClient = useQueryClient();
  const {
    data: messages,
    isLoading,
    error,
    refetch,
  } = useQuery(
    trpc.chat.listChatThreadMessages.queryOptions(
      { threadId: threadId ?? "" },
      {
        enabled: open && Boolean(threadId),
        staleTime: 0,
        refetchInterval: () => 5_000 + Math.random() * 5_000,
      },
    ),
  );

  const sendMessageMutation = useMutation(
    trpc.chat.sendMessage.mutationOptions({
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries(
          trpc.chat.listChatThreadMessages.queryOptions({
            threadId: variables.threadId,
          }),
        );
      },
      onError: () => {
        toast.error("Unable to send message. Please try again.");
      },
    }),
  );

  const handleSendMessage = async (
    message: string,
    attachmentIds?: string[],
  ) => {
    if (!threadId) {
      toast.error("Chat is not ready yet. Please try again.");
      return;
    }

    await sendMessageMutation.mutateAsync({
      threadId,
      message,
      attachmentIds,
    });
  };

  const displayName =
    [user.firstName, user.lastName]
      .filter((part) => part && part.length > 0)
      .join(" ")
      .trim() || "Provider";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg md:max-w-xl lg:max-w-2xl h-[85vh] max-h-dvh w-lg flex flex-col p-0 shadow-2xl rounded-lg">
        <DialogHeader className="border-b p-4">
          <DialogTitle className="text-lg font-medium">
            {displayName}
          </DialogTitle>
          <DialogDescription>
            Chat with this provider from the current business unit.
          </DialogDescription>
        </DialogHeader>
        {!threadId || isLoading ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="flex flex-col items-center text-center">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-3" />
              <p className="text-gray-600">Loading conversation...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <Alert variant="destructive" className="max-w-md">
              <AlertDescription>
                There was an error loading this conversation. Please try again.
              </AlertDescription>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </Alert>
          </div>
        ) : (
          <ChatThreadBody
            messages={messages ?? []}
            onSendMessage={handleSendMessage}
            readOnly={false}
            threadId={threadId ?? ""}
            viewMode="org"
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/provider-search/",
)({
  component: ProviderSearch,
  validateSearch: (search: Record<string, string>) => ({
    lat: search.lat,
    lng: search.lng,
    cityName: search.cityName,
    specialty: search.specialty,
    specialties: search.specialties,
    distance: search.distance,
    employmentTypeIds: search.employmentTypeIds,
    professionalTypeId: search.professionalTypeId,
    professionalTypeName: search.professionalTypeName,
    professionalTypes: search.professionalTypes,
    stateLicenses: search.stateLicenses as string | undefined,
  }),
});
